import type { Align } from './prefs';
import { collectTextBlocks } from './text-blocks';

/**
 * Text alignment. The CSS rule in css/main.css (keyed off data-ea11y-align,
 * which the pre-paint snippet sets) covers paragraphs, headings, list items
 * and table cells before first paint. Themes also put text straight inside a
 * <div> (infographic numbers, title boxes, hero captions), which no tag list
 * can reach without also moving layout boxes. So once the bundle runs, every
 * box that actually lays out a line of text gets the alignment inline, as
 * `!important`, and boxes that only hold other boxes are left alone.
 *
 * Links and buttons styled as blocks keep their own alignment: a centered
 * label is part of the button's design, not a paragraph.
 *
 * Every change restores the original inline values first, so switching
 * between left, center and right never stacks.
 *
 * Known limit (ticket §6): a heading in a flex column with
 * `align-items: center` is only as wide as its text, so there is no room for
 * left or right to move it. Same as every overlay.
 */

const KEEP_OWN =
  'a, button, [role="button"], [role="tab"], [role="menuitem"], [role="switch"]';

interface Original {
  inline: string;
  priority: string;
}

let current: Align = 'none';
const originals = new Map<HTMLElement, Original>();
let observer: MutationObserver | null = null;
let pending: Element[] = [];
let frame = 0;

export function setTextAlign(next: Align): void {
  if (next === current) return;
  restoreAll();
  current = next;
  if (next === 'none') {
    stopObserving();
    return;
  }
  if (document.body) alignIn(document.body);
  startObserving();
}

function alignIn(root: Element): void {
  const within = document.body || root;
  collectTextBlocks(root, { within, includeHidden: true }).forEach(({ el }) => {
    if (originals.has(el) || el.matches(KEEP_OWN)) return;
    originals.set(el, {
      inline: el.style.getPropertyValue('text-align'),
      priority: el.style.getPropertyPriority('text-align'),
    });
    el.style.setProperty('text-align', current, 'important');
  });
}

function restoreAll(): void {
  originals.forEach((original, el) => {
    if (original.inline) {
      el.style.setProperty('text-align', original.inline, original.priority);
    } else {
      el.style.removeProperty('text-align');
    }
  });
  originals.clear();
}

function startObserving(): void {
  if (observer || typeof MutationObserver === 'undefined' || !document.body)
    return;
  observer = new MutationObserver((records) => {
    records.forEach((record) => {
      record.addedNodes.forEach((node) => {
        if (node.nodeType === 1) pending.push(node as Element);
        else if (node.nodeType === 3 && node.parentElement)
          pending.push(node.parentElement);
      });
    });
    if (pending.length && !frame) frame = window.requestAnimationFrame(flush);
  });
  // childList only: our own style writes are attribute changes and never re-trigger this.
  observer.observe(document.body, { childList: true, subtree: true });
}

function stopObserving(): void {
  if (observer) observer.disconnect();
  observer = null;
  pending = [];
  if (frame) window.cancelAnimationFrame(frame);
  frame = 0;
}

function flush(): void {
  frame = 0;
  const batch = pending;
  pending = [];
  if (current === 'none') return;
  batch.forEach((root) => {
    if (root.isConnected) alignIn(root);
  });
  // Drop boxes that left the page, so a long visit on a busy page does not grow forever.
  if (originals.size > 2000) {
    originals.forEach((_, el) => {
      if (!el.isConnected) originals.delete(el);
    });
  }
}
